import React, { useContext } from "react";
import {
  AppBar,
  Box,
  IconButton,
  Stack,
  ThemeProvider,
  Tooltip,
  Toolbar,
  Typography,
} from "@mui/material";
import ReactToPrint from "react-to-print";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCloudArrowDown } from "@fortawesome/free-solid-svg-icons";
import { DataContext } from "../../context/DataContext";
import { getRol, getUser } from "../../api/Account";
import { crearTema } from "../../theme/theme";
import { ParametrosReporte } from "./ParametrosReporte";

/**
 * Hoja de estilo de impresión común de los reportes.
 * @param {object} [opciones]
 * @param {"landscape"|"portrait"} [opciones.orientacion] Por defecto "landscape".
 * @param {number} [opciones.margenMm] Margen de la hoja en mm; mínimo 5 (por defecto 8).
 */
export const estiloImpresion = ({ orientacion = "landscape", margenMm = 8 } = {}) => {
  const margen = Math.max(5, Number(margenMm) || 8);
  return `
  @media print {
    html, body {
      height: initial !important;
      overflow: initial !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    tr { break-inside: avoid; page-break-inside: avoid; }
    thead { display: table-header-group; }
  }
  @page { size: ${orientacion === "portrait" ? "portrait" : "landscape"}; margin: ${margen}mm; }
`;
};

// Ancho de un IconButton mediano con icono de 22 px (22 + 2 x 8 de relleno).
const ESPACIO_BOTON = 38;

/**
 * Encabezado de pantalla de un reporte: barra con logo y nombre de la empresa, botones de
 * Excel e imprimir, título del reporte, parámetros y usuario con fecha y hora.
 * Va dentro del Dialog del reporte, antes de la tabla.
 *
 * @param {object} props
 * @param {string} props.titulo Título del reporte; también es el nombre del documento al imprimir.
 * @param {{label: string, valor: React.ReactNode}[]} [props.parametros]
 * @param {() => void} [props.onExportar] Genera el Excel (cada reporte conserva el suyo). Sin ella no se
 *   dibuja el botón de Excel, pero queda un espacio del mismo ancho para no mover el título.
 * @param {React.RefObject} [props.impresionRef] Ref de la <HojaImpresion> que se imprime. Obligatoria salvo con `sinImprimir`.
 * @param {boolean} [props.sinImprimir] Oculta el botón de imprimir (deja su espacio). Por defecto false.
 * @param {"landscape"|"portrait"} [props.orientacion] Por defecto "landscape".
 * @param {number} [props.margenMm] Margen de impresión en mm; mínimo 5 (por defecto 8).
 * @param {string} [props.usuario] Por defecto, el usuario de la sesión.
 * @param {string} [props.rol] Por defecto, el rol de la sesión.
 */
export const EncabezadoReporte = ({
  titulo,
  parametros = [],
  onExportar,
  impresionRef,
  sinImprimir = false,
  orientacion = "landscape",
  margenMm = 8,
  usuario = getUser(),
  rol = getRol(),
}) => {
  const { title, slogan, icon } = useContext(DataContext);

  return (
    <>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <img loading="lazy" src={icon} alt="" style={{ height: 40 }} />
          <Typography sx={{ ml: 2, flex: 1, textAlign: "center" }} variant="h5" component="div">
            {title}
          </Typography>
          <img loading="lazy" src={icon} alt="" style={{ height: 40 }} />
        </Toolbar>
      </AppBar>

      <Stack sx={{ px: { xs: 2, sm: 3 } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 1, mb: 1 }}
        >
          {onExportar ? (
            <Tooltip title="Exportar a Excel">
              <IconButton
                aria-label="Exportar a Excel"
                onClick={onExportar}
                sx={{ color: "primary.main" }}
              >
                <FontAwesomeIcon icon={faCloudArrowDown} style={{ fontSize: 22, width: 22 }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Box aria-hidden sx={{ width: ESPACIO_BOTON, height: ESPACIO_BOTON }} />
          )}

          <Stack>
            <Typography sx={{ color: "text.secondary", textAlign: "center", fontWeight: "bold" }}>
              {slogan}
            </Typography>
            <Typography
              sx={{ color: "primary.main", textAlign: "center", fontWeight: "bold" }}
              variant="h6"
              component="h1"
            >
              {titulo}
            </Typography>
          </Stack>

          {sinImprimir ? (
            <Box aria-hidden sx={{ width: ESPACIO_BOTON, height: ESPACIO_BOTON }} />
          ) : (
            <ReactToPrint
              trigger={() => (
                <Tooltip title="Imprimir reporte">
                  <IconButton aria-label="Imprimir reporte" sx={{ color: "primary.main" }}>
                    <PrintRoundedIcon style={{ fontSize: 22, width: 22 }} />
                  </IconButton>
                </Tooltip>
              )}
              content={() => impresionRef.current}
              documentTitle={titulo}
              pageStyle={estiloImpresion({ orientacion, margenMm })}
            />
          )}
        </Stack>

        <ParametrosReporte parametros={parametros} usuario={usuario} rol={rol} tamano={12} />
      </Stack>
    </>
  );
};

// La hoja impresa siempre usa el theme claro: negro sobre blanco aunque la pantalla esté en modo oscuro.
const temaImpresion = crearTema("light");

/**
 * Hoja que se imprime: logo, empresa, título, parámetros y usuario, y debajo el contenido
 * (`children`, normalmente la copia de la tabla). Va oculta en pantalla y su ref se pasa como
 * `impresionRef` al <EncabezadoReporte>.
 *
 * @param {object} props
 * @param {string} props.titulo
 * @param {{label: string, valor: React.ReactNode}[]} [props.parametros]
 * @param {string} [props.usuario]
 * @param {string} [props.rol]
 */
export const HojaImpresion = React.forwardRef(
  ({ titulo, parametros = [], usuario = getUser(), rol = getRol(), children }, ref) => {
    const { title, slogan, icon } = useContext(DataContext);

    return (
      <ThemeProvider theme={temaImpresion}>
        <div ref={ref} style={{ color: "#000", backgroundColor: "#fff" }}>
          <Stack justifyContent="space-between" alignItems="center" direction="row">
            <img loading="eager" src={icon} alt="" style={{ height: 50 }} />
            <Stack justifyContent="center">
              <Typography sx={{ textAlign: "center", fontWeight: "bold", fontSize: 15, color: "#000" }}>
                {title}
              </Typography>
              <Typography sx={{ textAlign: "center", fontWeight: "bold", fontSize: 12, color: "#000" }}>
                {slogan}
              </Typography>
              <Typography sx={{ textAlign: "center", fontWeight: "bold", fontSize: 11, color: "#000" }}>
                {titulo}
              </Typography>
            </Stack>
            <img loading="eager" src={icon} alt="" style={{ height: 50 }} />
          </Stack>

          <Box sx={{ mt: 1, px: 1 }}>
            <ParametrosReporte
              parametros={parametros}
              usuario={usuario}
              rol={rol}
              tamano={10}
              colorEtiqueta="#000"
            />
          </Box>

          <div style={{ fontSize: 8 }}>{children}</div>
        </div>
      </ThemeProvider>
    );
  }
);

export default EncabezadoReporte;
